import React, { useCallback, useEffect, useState } from "react";

import { getEmissionFactors, updateAllFootprints, updateEmissionFactors } from "services";

import { incomplete, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminSection, Alert, Button, Col, Form, Input, InputNumber, MainPage, Row, Message as message } from "components";

export const PlatformEmissionFactors = () => {
  const [loading, setLoading] = useState(true);
  const [emissionFactors, setEmissionFactors] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const [form] = Form.useForm();

  const loadEmissionFactors = () => {
    setLoading(true);

    getEmissionFactors({
      signal,
    })
      .then((res) => {
        setEmissionFactors(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateSomeFootprints = useCallback(
    (start) => {
      updateAllFootprints({
        signal,
        start,
      }).then((res) => {
        if (res.updatedFieldModuleCount) {
          message({
            content: `The gremlins have successfully recalculated footprints of ${res.updatedFieldModuleCount} field modules.`,
            icon: "gremlin",
          });
        }

        if (start >= res.total) {
          setLoading(false);
        } else {
          updateSomeFootprints(start + 100);
        }
      });
    },
    [signal]
  );

  const complete = (v) => {
    setLoading(true);

    const values = Object.entries(v).reduce((acc, [key, value]) => {
      const property = key.slice(0, -2);
      const country = key.slice(-2).toLowerCase();
      acc[property] = { ...acc[property], [country]: value };

      return acc;
    }, {});

    updateEmissionFactors({
      ...values,
      ...{ signal },
    })
      .then(() => {
        message({
          content: "Platform emission factors successfully updated. Recalculating footprints...",
          icon: "gremlin",
        });
        loadEmissionFactors();
      })
      .finally(() => {
        updateSomeFootprints(0);
      });
  };

  useEffect(() => {
    loadEmissionFactors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage
      loading={loading}
      pageName={"admin"}
      beforePage={
        <Alert
          className="alert-banner center sticky"
          description="Proceed with caution. Modifying data here may have broad or unexpected impacts on the platform and users."
          icon=""
          closable={false}
          style={{ backgroundColor: "#ff845e" }}
        />
      }
    >
      <AdminHead headline="Update emission factors" description={<>This will trigger a recalculation of all footprints.</>} />

      <Form form={form} name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
        {Object.entries(emissionFactors).map(([key, value]) => {
          const InputComponent = key === "hpCalcsDistances" ? Input : InputNumber;
          return (
            <>
              <AdminSection
                headline={key}
                description={
                  <>
                    Update this emission factor - click save to submit the data.
                    <br />
                    <br />
                  </>
                }
                extra={
                  <Row>
                    {/* prelint-ignore-col */}
                    {value.de && (
                      <Col sm={12} md={12}>
                        <InputComponent
                          name={`${key}DE`}
                          label="DE"
                          rules={[{ required: true }]}
                          initialValue={value.de}
                          className="no-suffix"
                          precision={5}
                          step={0.00001}
                        />
                      </Col>
                    )}
                    {value.pl && (
                      <Col sm={12} md={12}>
                        <InputComponent
                          name={`${key}PL`}
                          label="PL"
                          rules={[{ required: true }]}
                          initialValue={value.pl}
                          className="no-suffix"
                          precision={5}
                          step={0.00001}
                        />
                      </Col>
                    )}
                  </Row>
                }
              />
            </>
          );
        })}

        <Row className="margin-top-200">
          <Col>
            <Form.Item>
              <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                Update settings
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </MainPage>
  );
};
